// Client profile → Tax: tax-exempt flag, certificate expiry, and the
// certificate file. Mirrors COISection's shape. The certificate lives in a
// PRIVATE storage bucket and is only ever opened through a short-lived
// signed URL from the API (GET /api/rentals/clients/:id/tax-certificate).
import React, { useState, useEffect, useCallback } from 'react';
import axios from './lib/axiosClient';

const fmtDate = d => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '—';

export default function TaxExemptSection({ clientId }) {
  const [client, setClient] = useState(null);
  const [form, setForm]     = useState({ tax_exempt: false, tax_exempt_expiry_date: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState('');
  const [err, setErr]       = useState('');
  const [file, setFile]     = useState(null);

  const load = useCallback(async () => {
    if (!clientId) return;
    try {
      const r = await axios.get(`/api/rentals/clients/${clientId}`);
      const c = r.data.client || r.data;
      setClient(c);
      setForm({ tax_exempt: !!c.tax_exempt, tax_exempt_expiry_date: c.tax_exempt_expiry_date || '' });
    } catch (e) { setErr(e.response?.data?.error || 'Could not load client'); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const today = new Date().toISOString().slice(0, 10);
  const expired = form.tax_exempt && form.tax_exempt_expiry_date && form.tax_exempt_expiry_date < today;
  const active  = form.tax_exempt && !expired;

  async function save() {
    setSaving(true); setErr(''); setMsg('');
    try {
      await axios.put(`/api/rentals/clients/${clientId}`, { tax_exempt: form.tax_exempt, tax_exempt_expiry_date: form.tax_exempt_expiry_date || null });
      if (file) {
        const fd = new FormData(); fd.append('file', file);
        await axios.post(`/api/rentals/clients/${clientId}/tax-certificate`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setFile(null);
      }
      setMsg('Saved'); setTimeout(() => setMsg(''), 2500);
      await load();
    } catch (e) { setErr(e.response?.data?.error || 'Save failed'); }
    setSaving(false);
  }
  async function openCertificate() {
    try {
      const r = await axios.get(`/api/rentals/clients/${clientId}/tax-certificate`);
      window.open(r.data.url, '_blank', 'noopener');
    } catch (e) { setErr(e.response?.data?.error || 'Could not open certificate'); }
  }
  async function removeCertificate() {
    if (!window.confirm('Remove the certificate file?')) return;
    try { await axios.delete(`/api/rentals/clients/${clientId}/tax-certificate`); await load(); }
    catch (e) { setErr(e.response?.data?.error || 'Could not remove certificate'); }
  }

  const S = {
    card: { background:'#0f0f1a', border:'1px solid #252840', borderRadius:10, padding:20, marginBottom:16 },
    lbl:  { fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'.4px', display:'block', marginBottom:4 },
    inp:  { padding:'8px 10px', borderRadius:6, border:'1px solid #2e3150', background:'#181928', color:'#e2e8f0', fontSize:13, width:'100%', boxSizing:'border-box' },
    btn:  { padding:'9px 16px', borderRadius:7, border:'none', background:'#f97316', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' },
    ghost:{ padding:'7px 12px', borderRadius:6, border:'1px solid #2e3150', background:'transparent', color:'#60a5fa', fontSize:12, cursor:'pointer' },
  };

  return (
    <div style={S.card}>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
        <div style={{ fontWeight:700, color:'#e2e8f0' }}>🧾 Tax Exemption</div>
        {form.tax_exempt && (
          <span style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:4, background: active ? 'rgba(74,222,128,0.12)' : 'rgba(248,113,113,0.12)', color: active ? '#4ade80' : '#f87171' }}>
            {active ? 'EXEMPT' : 'EXPIRED — TAXABLE AGAIN'}
          </span>
        )}
      </div>
      {err && <div style={{ color:'#f87171', fontSize:12, marginBottom:10 }}>{err}</div>}
      {msg && <div style={{ color:'#4ade80', fontSize:12, marginBottom:10 }}>{msg}</div>}

      <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:'#e2e8f0', cursor:'pointer', marginBottom:14 }}>
        <input type="checkbox" checked={form.tax_exempt} onChange={e => setForm(p => ({ ...p, tax_exempt: e.target.checked }))} style={{ accentColor:'#f97316' }} />
        This client is tax-exempt (resale / non-profit / government certificate on file)
      </label>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
        <div>
          <label style={S.lbl}>Certificate expiry</label>
          <input type="date" style={S.inp} value={form.tax_exempt_expiry_date} onChange={e => setForm(p => ({ ...p, tax_exempt_expiry_date: e.target.value }))} disabled={!form.tax_exempt} />
          <div style={{ fontSize:11, color:'#64748b', marginTop:4 }}>Leave blank if the certificate does not expire. After this date the client is taxed again automatically.</div>
        </div>
        <div>
          <label style={S.lbl}>Certificate file (PDF or image)</label>
          {client?.tax_exempt_certificate_path ? (
            <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:8 }}>
              <button type="button" onClick={openCertificate} style={S.ghost}>📄 Open certificate</button>
              <button type="button" onClick={removeCertificate} style={{ ...S.ghost, color:'#f87171' }}>Remove</button>
            </div>
          ) : <div style={{ fontSize:12, color:'#64748b', marginBottom:8 }}>No certificate on file.</div>}
          <input type="file" accept="application/pdf,image/*" onChange={e => setFile(e.target.files?.[0] || null)} disabled={!form.tax_exempt} style={{ fontSize:12, color:'#94a3b8' }} />
          <div style={{ fontSize:11, color:'#64748b', marginTop:4 }}>Stored privately; opened only through a link that expires in 5 minutes.</div>
        </div>
      </div>

      {form.tax_exempt && (
        <div style={{ fontSize:12, color:'#94a3b8', marginTop:12 }}>
          Expires: <strong style={{ color: expired ? '#f87171' : '#e2e8f0' }}>{form.tax_exempt_expiry_date ? fmtDate(form.tax_exempt_expiry_date) : 'no expiry'}</strong>
        </div>
      )}

      <div style={{ display:'flex', justifyContent:'flex-end', marginTop:14 }}>
        <button onClick={save} disabled={saving} style={{ ...S.btn, opacity: saving ? 0.6 : 1 }}>{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </div>
  );
}
